import { EditType } from 'src/types/edit';
import { InsightSummaryPage } from 'src/types/pages/insights';

export const REMOTE_WORKING_EXPERIENCE: InsightSummaryPage = {
  page_id: 'insights-27',
  section_order: ['header', 'summary'],
  header: {
    visible: true,
    content: {
      title: 'Remote Working Experience',
      body: 'Everyone, everywhere, all the time. The signature experience of remote work for mediators is the remarkable number of connections mediators can make almost simultaneously.',
      header_type: 'full',
    },
  },
  summary: {
    visible: true,
    content: {
      highlight_link_style: {
        default_color: '#11304B',
        active_color: '#48bf53',
      },
      paragraphs: [
        {
          label: 'Overview & Recommendations',
          text: 'The remote working experience is seen as both an opportunity and a challenge by mediators.  Some have <Embed getEmbedInfo={getEmbedInfo}>embraced-5250617</Embed> the remote working environment to elevate the quality of their mediation practice, with many mediators stepping out of their comfort zone to develop their skills and confidence in navigating the remote working experience. However, there are others who have either been <Embed getEmbedInfo={getEmbedInfo}>resistant-5250399</Embed> to changes or struggled with the learning curve of virtual meeting technologies and are having trouble finding their rhythm in this new working world. <br/><br/>Recommendations from conversations include:<br/><ul><li>Field operations management creates and maintains an environment of encouragement for challenged learners and continues to offer the support and training needed for all to become more <Embed getEmbedInfo={getEmbedInfo}>comfortable-5248965</Embed> and competent in navigating and providing FMCS services in the remote working environment.</li><li>Field operations management is held accountable and holds field mediators accountable for learning and incorporating remote meeting technologies and virtual meeting tools into their respective <Embed getEmbedInfo={getEmbedInfo}>practices and job duties-5249093</Embed>.</li><li>The Agency continues to explore and pilot new approaches and innovative technologies to continuously improve and be a proactive leader in utilizing technology to build relationships and provide a satisfying <Embed getEmbedInfo={getEmbedInfo}>remote working-5249116</Embed> experience for the clients and the FMCS staff.</li></ul>',
        },
        {
          label: 'Workflow',
          text: "For many mediators adjusting to the new normal of at-home or hybrid work, there are new routines and rhythms. As <Embed getEmbedInfo={getEmbedInfo}>Speaker AC-5249109</Embed> says: “We may go all day, we may go a couple of hours, and then I may or may not be able to get another meeting on that day.” To address this uncertainty, mediators actively coordinate with each other and broker agreements on workflow: “And through today, we generally will cap it and say, “Look, we've got six hours together. How are we going to best utilize our time?””<br/><br/>In essence, coordination, cooperation, and the judicious use of technology play a large role in creating workflow efficiency. Prior to the Covid-era lockdowns, scheduling had been generally challenging, but <Embed getEmbedInfo={getEmbedInfo}>Speaker JB-5249325</Embed> points out that after some practice on the virtual platforms: “We could look at each other’s calendars and figure out the 45 minutes we needed are here on this and such day and all of us will still get to go home at a reasonable hour kind of a thing.”<br/><br/>Some participants describe how the temptation to get more done can become so compelling that work overflows traditional meeting times and spaces. <Embed getEmbedInfo={getEmbedInfo}>Speaker TV-5249441</Embed> sums this up: “There's the ability to be in several places at once.” On occasion, the level of multi-tasking can reach unheard of heights, where “... people who were literally in two meetings at once on two different devices.”<br/><br/>Working on the sequence to initiate, maintain, and then complete their work also requires some juggling as noted by <Embed getEmbedInfo={getEmbedInfo}>Speaker SC-5250629</Embed> “…interactive, on demand and live. Interactive, yes, pull out your phones, open your cameras, save that link…” and the best way to smooth the process is to share their case work and as <Embed getEmbedInfo={getEmbedInfo}>Speaker CA-5250339</Embed> advises that everyone must, “...ask each other for help.”",
        },
        {
          label: 'Impersonal',
          text: "One of the largest perceived losses in the wake of fully virtual or hybrid negotiations stem from not being able to “read the room” as easily as when the mediator is physically present. <Embed getEmbedInfo={getEmbedInfo}>Speaker MB-5248971</Embed> and <Embed getEmbedInfo={getEmbedInfo}>Speaker TA-5248970</Embed> describe the disorientation when they are missing the full body experience of witnessing a client interact with the immediate world around them “We're missing the way people shuffle around at a meeting, or if they're tapping their pencil,” as noted by <Embed getEmbedInfo={getEmbedInfo}>Speaker MB-5248971</Embed>.<br/><br/>Hybrid meetings, where some parties are meeting in person with others joining virtually, can present a particular set of challenges.  <Embed getEmbedInfo={getEmbedInfo}>Speaker BA-5250345</Embed> describes the alienating feeling when the mediator is the only one not present: “When I'm on the screen and everybody else is in the room, I've lost my instinctive feel for what's going on.”  <br/><br/>Participants point out that losing the ability to gauge immediate reactions during negotiations is not the only casualty of the virtual experience; distractions are at an all time high.<Embed getEmbedInfo={getEmbedInfo}>Speaker AA-5250358</Embed> relays the experience of trying to work with a woman on a complex case and found out that in the process of negotiations,”...she was taking her kids back and forth to school and hockey practice and this happened while they were at caucus, she wasn't even in the meeting, she was in her car, so it was really a challenge.”",
        },
        {
          label: 'Learning Curve',
          text: "In their struggles with how to schedule and deploy their workflow and build authentic, virtual relationships in lieu of in-person meetings, most mediators note the “steep learning curve” and the “shift in mindset” as to how they learn, particularly when working with multiple applications. <Embed getEmbedInfo={getEmbedInfo}>Speaker AC-5249112</Embed> describes how “…my brain is still stuck in that other meeting, and I'm trying to pull up Google Docs to get my stuff in order because I didn't have time to get it all ready.”<br/><br/>Learning how to get up to speed with the virtual experience was especially difficult for new mediators, or those switching to new roles within the agency. One mediator, <Embed getEmbedInfo={getEmbedInfo}>Speaker DB-5249065</Embed> struggled with a team transition when Covid hit,  “So I had a new team. I didn't really have that support network that most mediators had, and a complete new structure”. <Embed getEmbedInfo={getEmbedInfo}>Speaker KC-5250432</Embed> describes how as a more novice mediator,  “You think you know what you're doing, but just getting that feedback and it's hard on a screen and then muddling your way through it, learning it and then learning how to do it virtually, they're not exactly the same.” <br/><br/><Embed getEmbedInfo={getEmbedInfo}>Speaker LB-5249019</Embed> feels empathy for new mediators who have not had a chance to see seasoned colleagues in action: “I would say, I feel sorry for the mediators that never really got an opportunity to shadow some others before they got thrown into the lion's den.”",
        },
        {
          label: 'Fatigue',
          text: "One of the most common themes from participants centers around exhaustion stemming from the multitude of meetings. <Embed getEmbedInfo={getEmbedInfo}>Speaker CB-5250450</Embed> highlights: “I've been in a few meetings where they have literally gone all day and the people may have had two 15-minute breaks and I don't know how they do it.” <br/><br/>The constant stream of activities that can occur in the virtual work environment where, as <Embed getEmbedInfo={getEmbedInfo}>Speaker BB-5248964</Embed> describes it, “...morning, noon, night, you were in negotiations or training or something and just physically, it was, especially last year, it was just really exhausting.” Mediators like <Embed getEmbedInfo={getEmbedInfo}>Speaker BB-5248964</Embed> combat exhaustion by using their best judgment to give one another breaks and to practice self-care.",
        },
      ],
    },
  },
  edit_metadata: {
    page_label: 'Remote Working Experience',
    page_id: 'insights-27',
    subroute: true,
    sections: [
      {
        section_label: 'Introduction',
        edit_blocks: [
          {
            block_label: 'Header',
            match: 'header.content',
            type: EditType.banner,
          },
        ],
      },
      {
        section_label: 'Highlight Link Colors',
        edit_blocks: [
          {
            block_label: 'Color',
            match: 'summary.content.highlight_link_style.default_color',
            type: EditType.color,
          },
          {
            block_label: 'Active Color',
            match: 'summary.content.highlight_link_style.active_color',
            type: EditType.color,
          },
        ],
      },
      {
        section_label: 'Paragraphs',
        update_section: {
          match: 'summary.content.paragraphs',
          label: 'Add Paragraph',
          empty: {
            label: 'Default Label',
            text: 'Default Text',
          },
          newSectionMetadata: {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: `summary.content.paragraphs[index].label`,
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: `summary.content.paragraphs[index].text`,
                type: EditType.embed_string,
              },
            ],
          },
        },
        sub_sections: [
          {
            section_label: 'Overview',
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[0].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[0].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[1].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[1].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[2].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[2].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[3].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[3].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[4].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[4].text',
                type: EditType.embed_string,
              },
            ],
          },
        ],
      },
    ],
  },
};
